import classNames from 'classnames';
import * as React from 'react';
import Icon from 'components/styleguide/icon/Icon';

const styles = require('./Button.module.scss');

export type ButtonContext = 'talent' | 'company' | 'neutral' | 'semantic';
export type ButtonType = 'primary' | 'secondary' | 'terceary';
export type ButtonSize = 'S' | 'M';

interface ButtonProps {
  'aria-label'?: string;
  'data-testid'?: string;
  children: React.ReactNode;
  className?: string;
  context?: ButtonContext;
  disabled?: boolean;
  fullWidth?: boolean;
  href?: string;
  iconLeft?: string;
  iconRight?: string;
  id?: string;
  onClick?: (event: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => void;
  onKeyDown?: React.KeyboardEventHandler<HTMLButtonElement | HTMLAnchorElement>;
  rel?: string;
  size?: ButtonSize;
  spinner?: boolean;
  target?: string;
  type?: ButtonType;
  typeAttr?: 'button' | 'submit' | 'reset';
}

const CONTEXT_ICON_COLOR: Record<ButtonContext, Record<ButtonType, string>> = {
  talent: { primary: 'neutral-border-white', secondary: 'talent-border-base', terceary: 'talent-border-base' },
  company: { primary: 'neutral-border-white', secondary: 'company-border-base', terceary: 'company-border-base' },
  neutral: { primary: 'neutral-border-white', secondary: 'neutral-border-darker', terceary: 'neutral-border-darker' },
  semantic: { primary: 'neutral-border-white', secondary: 'semantic-border-base', terceary: 'emantic-border-base' },
};

const Button: React.FC<ButtonProps> = ({
  'aria-label': ariaLabel,
  'data-testid': testId,
  children,
  className,
  context = 'neutral',
  disabled = false,
  fullWidth = false,
  href,
  iconLeft,
  iconRight,
  id,
  onClick,
  onKeyDown,
  rel,
  size = 'M',
  spinner,
  target,
  type = 'primary',
  typeAttr = 'button',
}) => {
  const hasLeftIcon = !!iconLeft;
  const hasRightIcon = !!iconRight;

  const iconColor = CONTEXT_ICON_COLOR[context][type];

  const renderIcon = (iconName: string | undefined): React.ReactNode => {
    if (!iconName) return null;

    return (
      <span className={styles['ui-button__icon']}>
        <Icon name={iconName} color={iconColor} size="sm" aria-hidden="true" />
      </span>
    );
  };

  const buttonClasses = classNames(
    styles['ui-button'],
    styles[`ui-button--context-${context}`],
    styles[`ui-button--type-${type}`],
    styles[`ui-button--size-${size.toLowerCase()}`],
    { [styles['ui-button--full-width']]: fullWidth },
    className
  );

  const commonProps = {
    'aria-label': ariaLabel,
    'data-testid': testId,
    'data-context': context,
    'data-type': type,
    'data-size': size,
    'data-spinner': spinner || undefined,
    className: buttonClasses,
    id,
  };

  const buttonContent = (
    <>
      {hasLeftIcon && renderIcon(iconLeft)}
      {children && <span className={styles['ui-button__text']}>{children}</span>}
      {hasRightIcon && renderIcon(iconRight)}
    </>
  );

  if (href) {
    const linkRel = rel || (target === '_blank' ? 'noopener noreferrer' : undefined);
    if (disabled) {
      return (
        <a
          {...commonProps}
          aria-disabled="true"
          tabIndex={-1}
          onClick={(event) => event.preventDefault()}
          onKeyDown={onKeyDown as React.KeyboardEventHandler<HTMLAnchorElement>}
        >
          {buttonContent}
        </a>
      );
    }
    return (
      <a
        href={href}
        target={target}
        rel={linkRel}
        onClick={onClick as React.MouseEventHandler<HTMLAnchorElement> | undefined}
        {...commonProps}
        onKeyDown={onKeyDown as React.KeyboardEventHandler<HTMLAnchorElement>}
      >
        {buttonContent}
      </a>
    );
  }

  return (
    <button
      type={typeAttr}
      onClick={onClick}
      disabled={disabled}
      {...commonProps}
      onKeyDown={onKeyDown as React.KeyboardEventHandler<HTMLButtonElement>}
    >
      {buttonContent}
    </button>
  );
};

export default Button;
